import { type ElementType, forwardRef } from 'react';

import classNames from 'classnames';

import styles from './Wave.module.css';

import type { ColorName } from '../../styles';
import { Color, getColor } from '../../styles';
import { WaveSvg } from './WaveSvg';

export type WaveMargin =
  | 'margin-xxs'
  | 'margin-xs'
  | 'margin-sm'
  | 'margin-md'
  | 'margin-lg'
  | 'margin-xl'
  | 'margin-xxl'
  | 'margin-3xl'
  | 'margin-4xl';

type IntrinsicDivProps = JSX.IntrinsicElements['div'];
export interface WaveProps extends IntrinsicDivProps {
  as?: ElementType;
  /** Optional class name */
  bottomClassName?: string;
  /** Optional class name */
  topClassName?: string;
  /** Optional class name */
  className?: string;
  /** Enable top wave */
  withTop?: boolean;
  /** Enable bottom wave */
  withBottom?: boolean;
  /** A transform to apply to the top wave */
  topTransform?: string;
  /** A transform to apply to the bottom wave */
  bottomTransform?: string;
  /** @defaultValue Color.Chickpea1 */
  fill?: Color | ColorName;
  /**
   * The height of the wave.
   *
   * @defaultValue 24
   */
  waveHeight?: string | number;
  /** The vertical padding of the wave content */
  padding?: WaveMargin;
}

/**
 * Render a container with waves at the top and the bottom.
 *
 * @see https://corgi-x.tfd.engineering/components/wave
 */
export const Wave = forwardRef<HTMLElement, WaveProps>(
  (
    {
      as = 'div',
      className,
      children,
      withTop,
      withBottom,
      topClassName,
      bottomClassName,
      fill = Color.Chickpea1,
      padding,
      waveHeight = 24,
      topTransform = 'scaleX(-1)',
      bottomTransform = 'scaleX(-1) rotate(180deg)',
      ...props
    },
    ref
  ) => {
    const fillColor = getColor(fill);
    const Component = as;
    return (
      <Component className={classNames(styles.container, className)} ref={ref}>
        {withTop && (
          <WaveSvg
            fill={fillColor}
            className={classNames(styles['top-wave'], topClassName)}
            style={{
              height: waveHeight,
              transform: topTransform,
            }}
          />
        )}
        <div
          className={classNames(
            styles.background,
            padding ? styles[padding] : null
          )}
          {...props}
          style={{ ...props.style, backgroundColor: fillColor }}
        >
          {children}
        </div>
        {withBottom && (
          <WaveSvg
            fill={fillColor}
            className={classNames(styles['bottom-wave'], bottomClassName)}
            style={{
              height: waveHeight,
              transform: bottomTransform,
            }}
          />
        )}
      </Component>
    );
  }
);
